/**
 * Shared helpers for retrying transient AI/Edge Function failures and
 * surfacing user-friendly bilingual messages when retries are exhausted.
 *
 * Used by both the text chat path (sendChatMessage) and the voice path
 * (sendVoiceMessage). Keeping the patterns and language detection in one
 * place means tuning the retry budget or message wording is a one-file edit.
 */
import { AI_ALLOWANCE_CODE } from './aiAllowance';
import { detectarIdioma, traducir } from '../i18n/idioma';

/**
 * Coerce whatever an upstream handed us into a string worth pattern-matching.
 *
 * `supabase.functions.invoke` is typed `<T = any>`, so `data.error` is
 * unchecked — and Google's own error shape is an OBJECT
 * (`{error:{code:429, message:"…", status:"RESOURCE_EXHAUSTED"}}`). When an
 * edge function forwards that verbatim, every downstream guard silently broke:
 * `RegExp.test(obj)` coerces to the literal "[object Object]", which matches
 * neither the retry nor the auth pattern, so a 429 got ZERO retries; and
 * `redactSecrets(obj)` then threw `text.replace is not a function` inside the
 * try block, surfacing a JavaScript internals string to the contractor as if
 * it were the AI's answer.
 *
 * Prefers a nested `message` because that is where the human-readable text and
 * the status code live in every provider shape we see.
 */
export function errorToText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    const nested = (o.error ?? o) as Record<string, unknown>;
    if (typeof nested?.message === 'string') {
      const status = typeof nested.status === 'string' ? ` ${nested.status}` : '';
      const code = typeof nested.code === 'number' ? ` ${nested.code}` : '';
      return `${nested.message}${code}${status}`.trim();
    }
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

/** Detect transient upstream failures worth retrying — Gemini overloaded,
 *  rate-limited, brief network blips, Edge Function cold-starts. Permanent
 *  errors (auth, validation) bail immediately so the user sees the real
 *  problem instead of waiting through retries. */
export function isRetryableAiError(detail: string): boolean {
  if (!detail) return false;
  return /\b503\b|\b429\b|UNAVAILABLE|overloaded|rate limit|RESOURCE_EXHAUSTED|temporarily|try again|Failed to (fetch|send a request)/i.test(detail);
}

/** Crude language detector — looks for common Spanish trigger words in the
 *  recent chat history. Good enough for an error toast. Defaults to Spanish
 *  since that's the primary contractor audience. */
export function friendlyAiBusyMessage(history: { role: string; text: string }[]): string {
  const recent = history.slice(-6).map(h => h.text).join(' ').toLowerCase();
  const looksSpanish = /\b(el|la|los|las|que|para|por|con|hacer|estimado|cliente|trabajo|hola|gracias|puedes|puedo)\b/.test(recent);
  return looksSpanish
    ? 'El asistente AI está ocupado en este momento. Por favor intente de nuevo en unos segundos.'
    : 'The AI assistant is busy right now. Please try again in a few seconds.';
}

/**
 * Strip credentials out of upstream error text before it can reach the UI.
 *
 * Google's 403 body embeds the offending key verbatim
 * (`Consumer 'api_key:AIza…' has been suspended`), and that text was being
 * rendered straight into the chat panel — exposing a server-side key to every
 * user who could trigger an AI error. Covers Google API keys, Supabase
 * publishable/secret/personal tokens, JWTs, and bearer headers.
 */
export function redactSecrets(text: string): string {
  // Total by construction: this is the last thing standing between an upstream
  // error body and the user's screen, so it must never be the thing that
  // throws. A non-string reaching here used to raise "text.replace is not a
  // function" and get reported as though it were the AI failure.
  if (typeof text !== 'string') return redactSecrets(errorToText(text));
  if (!text) return text;
  return text
    .replace(/AIza[0-9A-Za-z_-]{10,}/g, '[redacted-api-key]')
    .replace(/\bsb_(?:secret|publishable)_[0-9A-Za-z_-]+/g, '[redacted-key]')
    .replace(/\bsbp_[0-9A-Fa-f]{16,}/g, '[redacted-token]')
    // Groq and OpenAI reach the user through the voice path (Whisper STT is
    // Groq primary, OpenAI fallback), so their key shapes belong here too.
    .replace(/\bgsk_[0-9A-Za-z]{20,}/g, '[redacted-key]')
    .replace(/\bsk-proj-[0-9A-Za-z_-]{20,}/g, '[redacted-key]')
    .replace(/\bsk-[0-9A-Za-z_-]{20,}/g, '[redacted-key]')
    .replace(/\beyJ[0-9A-Za-z_-]{10,}\.[0-9A-Za-z_-]+\.[0-9A-Za-z_-]+/g, '[redacted-jwt]')
    .replace(/\b(Bearer|apikey)\s+[0-9A-Za-z._-]{12,}/gi, '$1 [redacted]');
}

/** Permanent credential/permission failures. These are an operator problem, not
 *  something the user can retry past, so they get a plain instruction instead of
 *  a raw provider JSON dump. */
export function isAiAuthError(detail: string): boolean {
  if (!detail) return false;
  return /\b401\b|\b403\b|PERMISSION_DENIED|UNAUTHENTICATED|API key not valid|has been suspended|API_KEY_INVALID/i.test(detail);
}

/** Bilingual message for a misconfigured or suspended AI key. */
export function friendlyAiAuthMessage(history: { role: string; text: string }[]): string {
  const recent = history.slice(-6).map(h => h.text).join(' ').toLowerCase();
  const looksSpanish = /\b(el|la|los|las|que|para|por|con|hacer|estimado|cliente|trabajo|hola|gracias|puedes|puedo)\b/.test(recent);
  return looksSpanish
    ? 'El asistente AI no está disponible: hay un problema con la configuración de la clave. Avise al administrador.'
    : 'The AI assistant is unavailable: there is a problem with its API key configuration. Please notify your administrator.';
}

/**
 * The monthly AI limit.
 *
 * When the platform reaches its monthly limit, every AI edge function answers
 * 402 with `code: 'ai_allowance_exhausted'`. That is neither a transient
 * failure nor a key problem, so it gets its own path:
 *  - never retried — three calls to hear "no" three times;
 *  - never logged with console.error/warn — both are shipped to
 *    system_error_logs, and this is the system working as designed;
 *  - told to the person in their language, with when it comes back.
 *
 * The code is read from the body's `code` field, never from the English text,
 * so the check survives any rewording of the server message.
 */
const AI_ALLOWANCE_MARKER = `[${AI_ALLOWANCE_CODE}]`;

/** The text of an AI function's error body — or the allowance marker when the
 *  body carries the allowance code. Use it wherever `body.error` was read. */
export function detailFromAiErrorBody(body: unknown, fallback: unknown): string {
  const b = body && typeof body === 'object' ? body as Record<string, unknown> : null;
  if (b?.code === AI_ALLOWANCE_CODE) return AI_ALLOWANCE_MARKER;
  return errorToText(b?.error ?? b?.debug_detail ?? fallback);
}

/** Check this BEFORE isRetryableAiError / isAiAuthError. */
export function isAiAllowanceError(detail: string): boolean {
  return typeof detail === 'string' && detail.includes(AI_ALLOWANCE_CODE);
}

/** "AI is paused until the 1st", in the interface language. */
export function friendlyAiAllowanceMessage(): string {
  return traducir(detectarIdioma(), 'ia.cupo.agotado');
}

/** Thrown by the one-shot AI calls (functions.ts) so a caller that shows its
 *  own fixed error text can still tell the limit apart from a real failure. */
export class AiAllowanceError extends Error {
  constructor() {
    super(friendlyAiAllowanceMessage());
    this.name = 'AiAllowanceError';
  }
}

/** Backoff delays between retries. Total worst-case wait = 2 + 4 = 6 seconds
 *  before showing the friendly busy message. Tuned for Gemini's "Spikes in
 *  demand are usually temporary" — short outages typically clear in <5s. */
export const RETRY_DELAYS_MS = [2000, 4000];
